import React, { useCallback, useMemo, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'

import { relativeOrDateTime } from 'utils'
import { useCanAccess, useReferrerState } from 'hooks'
import { useDemos, useVeraSpaceMutationsQuery } from 'hooks/api'
import { useVeraSpaceStore } from 'stores'
import { storage } from 'utils'
import { DEMO_TIER_LABELS } from 'constants/demos'
import { ROUTES } from 'routes'
import { DemoListItem } from 'services/demosApi'

import {
  ArrowUpRightIcon,
  BadgeCheckIcon,
  CalendarClockIcon,
  CheckIcon,
  CopyIcon,
  DoorOpenIcon,
  HourglassIcon,
  ImageIcon,
  MailIcon,
  PackagePlusIcon,
  RadarIcon,
  Trash2Icon,
  UserPlusIcon,
} from 'lucide-react'

import { demoInviteText } from './demoInvite'

import type { GridFilterModel } from '@mui/x-data-grid-pro'
// Tooltip from MUI, NOT @repo/ui — that one is a 0-byte stub that resolves in
// tsc and crashes at runtime.
import { Box, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material'

import {
  ActionDropdown,
  ActionDropdownItem,
  AddDemoProductsModal,
  CompanyAvatar,
  CreateDemoModal,
  EmptyState,
  ExtendDemoModal,
  ExtendTrialModal,
  Header,
  InviteDemoUserModal,
  OutreachCertificateModal,
  PurgeDemoModal,
  StatusChipCell,
  Table,
  TakeOverDemoModal,
  UserCell,
  headerWithTerm,
} from 'components'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { Button } from '@repo/ui'

import {
  DEMO_ENGAGEMENT_STATUSES,
  STALE_DEMO_AGE_DAYS,
  demoEngagementStatus,
  demoTrialStatus,
} from './demoStatuses'

/**
 * VRG-311 — the AE-facing acquisition demo console. Everything lives on this one
 * surface (AC1): list + create (URL -> propose -> review -> issue) + purge.
 *
 * The list is the entire mitigation for VRG-305 decision 9 (no automatic expiry —
 * AC4): age and never-opened state have to be visible here or nothing else makes
 * abandoned demos surface at all.
 */
export const InternalDemosPage = () => {
  const referrer = useReferrerState()
  const { isHasAccess: canProvisionDemo } = useCanAccess({ action: 'demo_provision' })

  // Purged rows are only fetched when the AE actually filters for them (mirrors
  // CompaniesPage's showArchived-from-Status-filter pattern) — the API defaults to
  // excluding them, and there's no reason to pay for that data on every load.
  const [includePurged, setIncludePurged] = useState(false)
  const { data: demos = [], isLoading, isRefetching } = useDemos(includePurged)

  const [createOpen, setCreateOpen] = useState(false)
  const [purgeTarget, setPurgeTarget] = useState<DemoListItem | null>(null)
  const [extendTarget, setExtendTarget] = useState<DemoListItem | null>(null)
  const [extendTrialTarget, setExtendTrialTarget] = useState<DemoListItem | null>(null)
  const [addProductsTarget, setAddProductsTarget] = useState<DemoListItem | null>(null)
  const [takeOverTarget, setTakeOverTarget] = useState<DemoListItem | null>(null)
  const [inviteTarget, setInviteTarget] = useState<DemoListItem | null>(null)
  // VRG-723 — the demo whose outreach certificate panel is open.
  const [outreachTarget, setOutreachTarget] = useState<DemoListItem | null>(null)

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    const statusItem = model.items.find((item) => item.field === 'engagementStatus')
    const selectedValues: string[] = Array.isArray(statusItem?.value) ? statusItem.value : []
    setIncludePurged(selectedValues.includes('purged'))
  }, [])

  const rows = useMemo(
    () =>
      demos.map((demo) => ({
        id: demo.companyId,
        ...demo,
        engagementStatus: demoEngagementStatus(demo),
      })),
    [demos]
  )

  const navigate = useNavigate()
  // Where "Return to Internal" comes back to: the page they were ACTUALLY on,
  // path + query so filters and pagination survive (§42). Detail pages included.
  const location = useLocation()
  const [copiedSubdomain, setCopiedSubdomain] = useState<string | null>(null)
  const [copiedInvite, setCopiedInvite] = useState<string | null>(null)
  const enterSpace = useVeraSpaceStore((s) => s.enter)
  const {enterAsync: fetchVeraGrant} = useVeraSpaceMutationsQuery({})

  /**
   * The bridge to the REAL product editor and to Studio.
   *
   * This is NO LONGER what "Add products" does (VRG-369). Adding a product to a
   * demo is four fields — title, sku, description, images — because that is all a
   * demo certificate renders, and paying a space entry to reach a ~20-field editor
   * for four of them is the wrong shape. That now happens in a modal on this page.
   *
   * The space loop survives as its own item because the editor is still the only
   * place with every field, section and validation, and Studio is the only place
   * to fix a bad scraped palette or font. `ProductEditPage`'s create sends NO
   * company_id — it writes to whatever company the session is scoped to — so
   * entering the demo's space first is the only way to use it, exactly as the
   * Companies page does for any other company.
   */
  const handleOpenDemoSpace = useCallback(
    async (row: DemoListItem) => {
      try {
        const grant = await fetchVeraGrant({companyId: row.companyId})
        // Land on Products rather than Settings — the reason to enter a DEMO's
        // space is its catalogue, not its configuration. No `openAddModal` flag
        // any more: the AE who chose "Go to space" over "Add products" wants the
        // editor, so opening a modal over the list on arrival would be fighting
        // the choice they just made.
        //
        // The destination is declared to the STORE as well as navigated to, because
        // `enter()` re-renders this page's own `InternalRoute` guard, which redirects
        // in-space staff away from internal routes with `replace` and lands after the
        // navigate below. Passing `landing` makes the guard agree rather than fight;
        // see the `landing` doc in useVeraSpaceStore (VRG-369).
        const landing = {path: ROUTES.PRODUCTS}
        enterSpace({
          companyId: row.companyId,
          companyName: grant.companyName,
          permissions: grant.permissions,
          landing,
          // Come back HERE, not to Companies (VRG-369). See the `returnTo` doc in
          // useVeraSpaceStore.
          returnTo: `${location.pathname}${location.search}`,
        })
        // Mirrors CompaniesPage: X-Vera-Company is derived from the veraSpace
        // store, but this key is set for parity with the rest of the app.
        storage.setItem('selectedCompany', row.companyId)
        navigate(landing.path)
      } catch (err) {
        console.error('Failed to enter demo space', err)
      }
    },
    [enterSpace, fetchVeraGrant, navigate],
  )

  const getActionItems = useCallback(
    (row: DemoListItem): ActionDropdownItem[] => {
    /**
     * VRG-361 Slice 2 — once a demo has been taken over, the company is a real
     * customer and every DEMO-LIFECYCLE action stops applying. This is not
     * cosmetic: `AddDemoProductsUseCase`, `ExtendDemoUseCase`,
     * `ExtendTrialUseCase` and `PurgeDemoCompanyUseCase` each refuse a company
     * whose `kind` is not `demo`, so leaving those rows visible would offer four
     * actions that all 403 — and "Purge demo" would be offering to delete a
     * paying customer's entire graph. Record-state unavailability -> hide (§46).
     *
     * "Go to space" and "Copy email draft" stay: entering a real company's space is
     * the same thing the Companies list offers (it now resolves the brand's own
     * consented grant, which is `[]` until they grant one — correct, not broken),
     * and the demo URL still resolves.
     */
    const isTakenOver = Boolean(row.demoTakenOverAt)
    /** Nothing at all is actionable on a row whose company has been deleted. */
    const isLive = !row.isPurged && !isTakenOver
    /**
     * VRG-361 Slice 2. `joinedUserCount`, NOT `invitedUserCount` — the invite writes
     * the membership row at send time, so the invited count moves before anybody has
     * opened anything, while the takeover requires a real sign-in.
     */
    const canTakeOver = isLive && row.joinedUserCount > 0

    return [
      {
        label: 'Add products',
        icon: <PackagePlusIcon strokeWidth={1} />,
        // Opens a modal ON this page. A demo product is four fields, so the AE
        // never leaves the console for the common case.
        onClick: () => setAddProductsTarget(row),
        // Record-state -> hide (§46): a purged demo has no company to add to, and
        // a taken-over one is no longer a demo, so the endpoint refuses it.
        visible: isLive,
      },
      {
        // The escape hatch to the FULL product editor and Studio, for the rarer
        // case the four-field modal cannot serve. Mirrors the Companies list,
        // which has had this item all along.
        label: 'Go to space',
        icon: <DoorOpenIcon strokeWidth={1} />,
        onClick: () => void handleOpenDemoSpace(row),
        // Record-state -> hide (§46): a purged demo has no company to enter.
        visible: !row.isPurged,
      },
      {
        /**
         * VRG-361 Slice 2 — the resolving action for the row below, and the thing
         * that makes the takeover reachable at all.
         *
         * The label changes rather than the row disappearing once somebody has been
         * invited, because re-inviting is a real and common move: an invitation that
         * went to the wrong address, or to one person when the deal now involves
         * two. `InviteCompanyUserUseCase` is idempotent per address (an existing
         * member is linked and re-invited, never duplicated) and the seat check
         * charges only for NEW people, so a resend is safe.
         *
         * Record-state -> hide (§46) on the two terminal states: a purged demo has
         * no company to invite into, and a taken-over one is a real customer whose
         * own admins invite from their own Users page (the endpoint refuses it too).
         */
        label: row.invitedUserCount > 0 ? 'Invite another admin' : 'Invite admin',
        icon: <UserPlusIcon strokeWidth={1} />,
        onClick: () => setInviteTarget(row),
        visible: isLive,
      },
      {
        // Copies the invite to the clipboard. It does NOT send: nothing in the
        // demo code sends mail, and the AE's own outbox is where the signature
        // and domain reputation already are. The text is written to become a
        // real templated invite later — see demoInvite.ts.
        label: copiedInvite === row.companyId ? 'Draft copied' : 'Copy email draft',
        icon:
          copiedInvite === row.companyId ? (
            <CheckIcon strokeWidth={1} />
          ) : (
            <MailIcon strokeWidth={1} />
          ),
        onClick: () => {
          void navigator.clipboard.writeText(demoInviteText(row))
          setCopiedInvite(row.companyId)
          window.setTimeout(() => setCopiedInvite(null), 2000)
        },
        // Record-state -> hide (§46): a purged demo's URL 404s, so an invite to
        // it would send the prospect to a dead page.
        visible: !row.isPurged,
      },
      {
        // VRG-723 — the one certificate the AE sends: copy its link, download its image for the
        // email, or (while it is still a demo) change which product it is.
        label: 'Outreach certificate',
        icon: <ImageIcon strokeWidth={1} />,
        onClick: () => setOutreachTarget(row),
        // Record-state -> hide (§46): a purged demo has no certificate left.
        visible: !row.isPurged,
      },
      {
        // The alternative to this action is purge-and-re-provision, which throws
        // away the engagement history that is the record's whole CRM value. A
        // Tier-1 package sitting unopened for three weeks is the normal case.
        label: 'Extend demo',
        icon: <CalendarClockIcon strokeWidth={1} />,
        onClick: () => setExtendTarget(row),
        // Record-state -> hide (§46): a purged demo has no company row left to
        // hold a lifetime, and a taken-over one is not a demo any more.
        visible: isLive,
      },
      {
        // The OTHER clock (§46 decision, made deliberately): this is the
        // invited admin's CONSOLE ACCESS deadline, not the demo certificate's
        // public lifetime above — "Extend demo" and "Extend trial" both stay
        // visible so the two are never confused for one action.
        //
        // Stays enabled even when the trial hasn't started yet
        // (trialStartedAt null) rather than hiding — ExtendTrialUseCase is
        // explicitly designed to work in that state, to pre-set a longer
        // deadline than the 14-day default (e.g. a Tier-1 physical send that
        // takes weeks to arrive). Hiding it would remove a real, documented
        // use of the endpoint. The only thing that hides this row is the
        // company itself being gone (isPurged), same as "Extend demo".
        label: 'Extend trial',
        icon: <HourglassIcon strokeWidth={1} />,
        onClick: () => setExtendTrialTarget(row),
        // …and once taken over the trial is moot: an active subscription always
        // overrides it (AssertConsoleTrialActiveUseCase), and the endpoint refuses
        // a non-demo company outright.
        visible: isLive,
      },
      {
        /**
         * VRG-361 Slice 2 — the takeover. One transaction: stamp, kind off demo,
         * billing on, seeded AE grant deleted, `-demo` rail released.
         *
         * ## §46, applied unmodified now that the resolving action exists
         *
         * This row used to be shown DISABLED with a tooltip when nobody from the
         * brand had signed in, as a documented deviation from §46's "record-state
         * unavailability HIDES". The deviation existed for one reason and one only:
         * §46's own conditional, *"Hiding creates an obligation. If a record-state
         * block hides an action, the action that RESOLVES that state must be
         * reachable in that state"* — and the resolving action did not exist in the
         * product, so hiding would have produced the un-catchable dead end §46 names
         * as its own residual risk.
         *
         * "Invite admin" above IS that action, in this same menu. The
         * obligation is met, so the rule applies as written and the block hides.
         *
         * Note what is deliberately NOT gated on here. The invite writes the
         * membership row immediately, so `invitedUserCount` moves the moment the AE
         * clicks send — but takeover needs somebody to have actually SIGNED IN, which
         * is `joinedUserCount`. Gating on the invited count would show this row for a
         * demo nobody has opened, and every click would 403.
         */
        label: 'Convert to account',
        icon: <BadgeCheckIcon strokeWidth={1} />,
        onClick: () => setTakeOverTarget(row),
        visible: canTakeOver,
        divider: true,
      },
      {
        label: 'Purge demo',
        icon: <Trash2Icon strokeWidth={1} />,
        onClick: () => setPurgeTarget(row),
        destructive: true,
        // The divider separates the DESTRUCTIVE/terminal pair from the working
        // actions above, and `divider` renders BEFORE its own item — so when
        // "Convert" is hidden it takes the separator with it and Purge would sit
        // flush against "Extend trial". Carried here in exactly that case, so the
        // grouping survives the row above disappearing.
        divider: !canTakeOver,
        // Record-state unavailability -> HIDE (§46): an already-purged demo has
        // nothing left to purge, and there is no path back from that state. A
        // taken-over company is a PAYING CUSTOMER — offering to delete its whole
        // graph from the demos console would be the worst row on this menu.
        visible: isLive,
      },
    ]
    },
    [handleOpenDemoSpace, copiedInvite]
  )

  const columns = useMemo(
    () => [
      {
        field: 'companyName',
        // 'Company', not 'Brand': the row IS a company row, at an earlier stage of
        // the pipeline. Calling it something else implied two entities.
        headerName: 'Company',
        // Required, not decorative: the filter panel derives its operators from
        // `type` and SKIPS any column that omits it, logging to the console and
        // showing nothing in the UI. Without this the console could not be
        // filtered by the name of the company each row is about.
        type: 'string',
        width: 220,
        sortable: false,
        renderCell: ({ row }: { row: DemoListItem }) =>
          row.isPurged ? (
            <Stack direction="row" spacing={3} alignItems="center" sx={{ height: '100%', opacity: 0.6 }}>
              <CompanyAvatar logo={null} name={row.companyName} size={40} />
              <Typography variant="body3" sx={{ fontWeight: 500 }} noWrap>
                {row.companyName}
              </Typography>
            </Stack>
          ) : (
            <Link
              component={RouterLink}
              to={ROUTES.INTERNAL_COMPANY(row.companyId)}
              state={referrer}
              underline="none"
              sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                {/* The scraped brand mark. This passed a hardcoded null and so
                    always fell back to initials — the whole point of a demo is
                    that it looks like the prospect's brand, and the list was the
                    one place that did not. Purged rows below stay null on
                    purpose: the storage object is gone. */}
                <CompanyAvatar logo={row.logo} name={row.companyName} size={40} />
                <Typography variant="body3" sx={{ fontWeight: 500 }} noWrap>
                  {row.companyName}
                </Typography>
              </Stack>
            </Link>
          ),
      },
      {
        field: 'subdomain',
        headerName: 'Subdomain',
        type: 'string',
        width: 260,
        renderCell: ({ row }: { row: DemoListItem }) => {
          // The demo is LIVE the moment it is issued (the certificate is inserted
          // active), so this link always resolves for an un-purged demo. A purged
          // one has no company left, so it is plain text rather than a dead link.
          // VRG-723 — the API's URL, on the customer app host for this environment.
          const href = row.demoUrl
          if (row.isPurged) {
            return (
              <Typography variant="body3" color="text.secondary">
                {row.subdomain}
              </Typography>
            )
          }
          return (
            <Stack direction="row" alignItems="center" spacing={1} height="100%">
              <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                variant="body3"
                onClick={(e) => e.stopPropagation()}
                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {row.subdomain}
              </Link>
              <Tooltip title={copiedSubdomain === row.subdomain ? 'Copied' : 'Copy demo link'}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    void navigator.clipboard.writeText(href)
                    setCopiedSubdomain(row.subdomain)
                    window.setTimeout(() => setCopiedSubdomain(null), 1500)
                  }}
                >
                  {copiedSubdomain === row.subdomain ? (
                    <CheckIcon size={14} strokeWidth={1} />
                  ) : (
                    <CopyIcon size={14} strokeWidth={1} />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
      {
        field: 'prospectDomain',
        headerName: 'Prospect',
        type: 'string',
        width: 220,
        renderCell: ({ row }: { row: DemoListItem }) => (
          <Stack sx={{ py: 2 }} justifyContent="center" height="100%">
            {row.prospectDomain ? (
              // Opens the prospect's REAL site, which is the thing an AE checks
              // when deciding whether the scrape got the right brand. External,
              // so a new tab with noopener — this is a third party's site and
              // must not get a handle on the console.
              <Link
                href={`https://${row.prospectDomain.replace(/^https?:\/\//i, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                variant="body3"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                // The row is not itself a link, but the cell sits inside a grid
                // that treats clicks as row interactions.
                onClick={(e) => e.stopPropagation()}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.prospectDomain}
                </Box>
                <ArrowUpRightIcon strokeWidth={1} size={14} style={{ flexShrink: 0 }} />
              </Link>
            ) : (
              <Typography variant="body3">—</Typography>
            )}
            {row.prospectContact && (
              <Typography variant="body3" color="text.secondary" noWrap>
                {row.prospectContact}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'sendTier',
        headerName: 'Tier',
        type: 'multiSelect',
        // Narrow: the value is "Tier 4" and the header is four characters, so the
        // floor here is the header plus its sort icon (§49), not the content.
        width: 110,
        renderCell: ({ value }: { value: string | null }) => (
          <Typography variant="body3">{value ? DEMO_TIER_LABELS[value] ?? value : '—'}</Typography>
        ),
      },
      // NO "Sent" COLUMN — deliberately removed 2026-08-22.
      //
      // It rendered `row.sentAt ?? row.provisionedAt`, and nothing ever writes
      // sentAt, so for every real row it fell through to the creation date and
      // showed a duplicate of "Created At" under a name that claimed more.
      //
      // A truthful Sent needs a send step, and there isn't one: no email exists
      // anywhere in the demo code. It comes back when "Copy email draft" grows into a
      // real invite that stamps sent_at. The column stays out until then rather
      // than displaying a proxy — sent_at is still stored and still used to
      // compute issued-to-first-open in the engagement tooltip.
      {
        field: 'engagementStatus',
        headerName: 'Engagement',
        type: 'statusChip',
        options: statusFilterOptions(DEMO_ENGAGEMENT_STATUSES),
        // Sized for the HEADER, not the chip (§49). The count lives INSIDE the
        // chip, so this no longer needs the 320 the beside-the-chip text did.
        width: 150,
        renderCell: ({ row }: { row: DemoListItem & { engagementStatus: string } }) => {
          const hasOpens = !row.isPurged && row.openCount > 0

          // The count belongs IN the chip rather than in text beside it: one
          // object instead of two, so the column shrinks and nothing truncates.
          // Overriding the label here rather than adding a prop to the shared
          // StatusChipCell keeps `engagementStatus` — and therefore the filter
          // options — on the bare status value.
          const statuses = hasOpens
            ? {
                ...DEMO_ENGAGEMENT_STATUSES,
                opened: {
                  ...DEMO_ENGAGEMENT_STATUSES.opened,
                  label: `Opened (${row.openCount})`,
                },
              }
            : DEMO_ENGAGEMENT_STATUSES

          // Everything time-based goes in the tooltip. The count is deliberately
          // NOT repeated here — the chip already carries it.
          //
          // This is a summary and not a chart on purpose: the audit row stores
          // first_opened_at, last_opened_at and an open_count integer, so opens
          // 2..n-1 have no timestamps. There is no series to plot without an
          // events table (which VRG-267's tour analytics needs too).
          const detail = hasOpens
            ? [
                row.firstOpenedAt ? `First: ${relativeOrDateTime(row.firstOpenedAt)}` : null,
                row.lastOpenedAt ? `Last: ${relativeOrDateTime(row.lastOpenedAt)}` : null,
                row.sentAt && row.firstOpenedAt
                  ? `Issued to first open: ${Math.max(
                      0,
                      Math.round(
                        (new Date(row.firstOpenedAt).getTime() - new Date(row.sentAt).getTime()) /
                          36e5,
                      ),
                    )}h`
                  : null,
              ]
                .filter(Boolean)
                .join('\n')
            : null

          // VRG-361 Slice 2 — the chip reads "Converted" and stops naming the open
          // count, so the tooltip is where that history has to survive: WHEN they
          // converted, and the engagement that led to it. Prepended rather than
          // appended because the conversion is the headline of the row.
          const detailWithTakeover = row.demoTakenOverAt
            ? [`Converted: ${relativeOrDateTime(row.demoTakenOverAt)}`, detail]
                .filter(Boolean)
                .join('\n')
            : detail

          const content = (
            <Stack direction="row" alignItems="center" height="100%">
              <StatusChipCell status={row.engagementStatus} statuses={statuses} />
            </Stack>
          )

          return detailWithTakeover ? (
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{detailWithTakeover}</span>}>
              {content}
            </Tooltip>
          ) : (
            content
          )
        },
      },
      {
        // The field is `provisionedAt`, NOT `ageDays`. Both sort identically
        // (age is monotonic with the date), but the filter control is derived
        // from `type`, so the earlier `type: 'number'` on ageDays gave this a
        // SLIDER over a day-count. A date column gets a date-range filter; the
        // numeric sort was not worth the wrong control.
        field: 'provisionedAt',
        // "Created At", not "Age" — the raw number was ambiguous about what it
        // counted from. The stale EMPHASIS is kept because AC4 depends on it: a
        // never-opened demo past the threshold has to read as a problem at a
        // glance.
        headerName: 'Created At',
        type: 'dateTime',
        // Wider: a full "August 20th 2026, 1:10 AM" was ellipsing at 170.
        width: 200,
        // AC4 is load-bearing here: a demo that's stale AND never opened is the
        // whole reason this column exists — it has to read as a problem at a
        // glance, not require opening the row.
        renderCell: ({ row }: { row: DemoListItem }) => {
          const isStale = !row.isPurged && row.neverOpened && row.ageDays >= STALE_DEMO_AGE_DAYS
          return (
            <Tooltip
              title={
                isStale
                  ? `Never opened, ${row.ageDays} days old — there is no automatic expiry, so this one only goes away if someone purges it.`
                  : `${row.ageDays} day${row.ageDays === 1 ? '' : 's'} old`
              }
            >
              <Typography
                variant="body3"
                sx={{ fontWeight: isStale ? 600 : 400 }}
                color={isStale ? 'error' : 'text.primary'}
              >
                {relativeOrDateTime(row.provisionedAt)}
              </Typography>
            </Tooltip>
          )
        },
      },
      {
        field: 'demoExpiresAt',
        headerName: 'Expiring',
        type: 'dateTime',
        // Narrower than it was: the cell now renders "14 days left" rather than a
        // full date, so 170 was sized for content that moved to the tooltip.
        width: 130,
        // Banding by urgency, not by prettiness: red under a week, yellow under
        // three, green beyond.
        //
        // "Never" is GREEN and that is deliberate — it is the DEFAULT and the
        // product decision (VRG-305 decision 9), not a missing value. Styling it
        // as a warning would train AEs to "fix" the intended state. It is also the
        // overwhelmingly common case, so it must read as unremarkable.
        renderCell: ({ row }: { row: DemoListItem }) => {
          if (row.isPurged) {
            return (
              <Typography variant="body3" color="text.secondary">
                —
              </Typography>
            )
          }
          if (!row.demoExpiresAt) {
            return (
              <Tooltip title="No expiry — the default. This demo lives until someone purges it.">
                <Typography variant="body3" color="success.main">
                  Never
                </Typography>
              </Tooltip>
            )
          }
          const days = Math.ceil(
            (new Date(row.demoExpiresAt).getTime() - Date.now()) / 864e5,
          )
          const color =
            days <= 7 ? 'error.main' : days <= 21 ? 'warning.main' : 'success.main'

          // DAYS LEFT in the cell, the DATE in the tooltip — the inverse of the
          // first pass. The question an AE is scanning this column for is "how
          // long have I got", and a date makes them do the subtraction on every
          // row. The exact date still matters when they are picking a follow-up
          // day, so it is one hover away rather than gone.
          const label =
            days < 0
              ? 'Expired'
              : days === 0
                ? 'Today'
                : `${days} day${days === 1 ? '' : 's'} left`

          return (
            <Tooltip
              title={
                days < 0
                  ? `Expired ${relativeOrDateTime(row.demoExpiresAt)}`
                  : `Expires ${relativeOrDateTime(row.demoExpiresAt)}`
              }
            >
              <Typography
                variant="body3"
                sx={{ fontWeight: days <= 7 ? 600 : 400 }}
                color={color}
              >
                {label}
              </Typography>
            </Tooltip>
          )
        },
      },
      {
        // VRG-361 — the invited admin's CONSOLE ACCESS trial. A DIFFERENT
        // clock from `demoExpiresAt` above (the public certificate's
        // lifetime) — see `demoTrialStatus` for why the two must never be
        // conflated. Same banding logic as `demoExpiresAt`, on its own
        // thresholds (the trial's own default span is 14 days, half of
        // demoExpiresAt's, so the urgency bands are tighter to match).
        field: 'trialEndsAt',
        headerName: 'Trial',
        // What the clock starts ON is the non-obvious part — the first invited admin
        // actually signing in, not the invite going out — and it is what an AE is
        // reasoning about when they look at this column.
        renderHeader: headerWithTerm('Trial', 'console-trial'),
        type: 'dateTime',
        // Sized for the HEADER, not the values (§49): "Trial" + the sort icon.
        // Matches "Tier" (110, a 4-char header) plus a little for the 5th
        // character — measured in-browser rather than estimated from length.
        // 130 -> 148 for the +18px info glyph headerWithTerm adds.
        width: 148,
        renderCell: ({ row }: { row: DemoListItem }) => {
          if (row.isPurged) {
            return (
              <Typography variant="body3" color="text.secondary">
                —
              </Typography>
            )
          }

          const status = demoTrialStatus(row)

          if (status === 'not_started') {
            return (
              <Tooltip
                title={
                  row.trialEndsAt
                    ? `Not started. Pre-set to end ${relativeOrDateTime(row.trialEndsAt)} once an admin accepts.`
                    : 'Not started. No admin has accepted the invite yet.'
                }
              >
                <Typography variant="body3" color="text.secondary">
                  Not started
                </Typography>
              </Tooltip>
            )
          }

          if (status === 'expired') {
            return (
              <Tooltip
                title={`Trial expired ${relativeOrDateTime(row.trialEndsAt!)}. Console access is blocked until the company subscribes.`}
              >
                <Typography variant="body3" sx={{ fontWeight: 600 }} color="error">
                  Expired
                </Typography>
              </Tooltip>
            )
          }

          // 'active' — trialEndsAt is always set once trialStartedAt is (the
          // start trigger stamps a default), so this is safe to assert.
          const days = Math.ceil(
            (new Date(row.trialEndsAt!).getTime() - Date.now()) / 864e5,
          )
          const color = days <= 3 ? 'error.main' : days <= 7 ? 'warning.main' : 'success.main'
          const label = days <= 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'} left`

          return (
            <Tooltip
              title={`Started ${relativeOrDateTime(row.trialStartedAt!)}. Console access ends ${relativeOrDateTime(row.trialEndsAt!)}.`}
            >
              <Typography
                variant="body3"
                sx={{ fontWeight: days <= 3 ? 600 : 400 }}
                color={color}
              >
                {label}
              </Typography>
            </Tooltip>
          )
        },
      },
      {
        field: 'provisionedByEmail',
        headerName: 'AE',
        // 'string' + searchParams is this codebase's searchable-multiselect (see
        // CertificatesPage's owner/product filters). A plain multiSelect lists every
        // distinct value with no way to type — fine for 4 statuses, wrong for a
        // growing list of people.
        //
        // AE and Vera Manager are deliberately NOT the same field: an AE owns the
        // ACQUISITION, a Vera Manager owns the account after conversion, and they
        // are often different people. See VRG-361.
        type: 'string',
        searchParams: { fields: ['provisionedByEmail'], preview: null },
        width: 220,
        renderCell: ({ row }: { row: DemoListItem }) => (
          <UserCell user={{ email: row.provisionedByEmail }} />
        ),
      },
      {
        field: 'actions',
        headerName: false,
        pinned: 'right',
        sortable: false,
        disableReorder: true,
        align: 'center',
        width: 54,
        renderCell: ({ row }: { row: DemoListItem }) => (
          <ActionDropdown items={getActionItems(row)} />
        ),
      },
    ],
    [getActionItems, referrer]
  )

  if (!canProvisionDemo) {
    return (
      <>
        <Header title="Demos" />
        <EmptyState
          title="You don't have access to this page"
          text="Ask an admin to grant the “Provision demos” permission."
          icon={<RadarIcon strokeWidth={1} size={32} />}
        />
      </>
    )
  }

  return (
    <>
      <Header
        title="Demos"
        rightComponents={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Create demo
          </Button>
        }
      />
      <Table
        fillBottom
        loading={isLoading || isRefetching}
        columns={columns as any}
        rows={rows}
        onFilterModelChange={handleFilterModelChange}
        emptyState={
          <EmptyState
            title="No demos yet"
            text="Paste a prospect's URL to propose a theme and products, review them, and issue a send-ready demo link."
            icon={<RadarIcon strokeWidth={1} size={32} />}
            buttons={
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                Create demo
              </Button>
            }
          />
        }
      />

      <CreateDemoModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <OutreachCertificateModal
        companyId={outreachTarget?.companyId ?? null}
        companyName={outreachTarget?.companyName}
        onClose={() => setOutreachTarget(null)}
      />
      <AddDemoProductsModal
        demo={addProductsTarget}
        open={!!addProductsTarget}
        onClose={() => setAddProductsTarget(null)}
      />
      <ExtendDemoModal
        demo={extendTarget}
        open={!!extendTarget}
        onClose={() => setExtendTarget(null)}
      />
      <ExtendTrialModal
        demo={extendTrialTarget}
        open={!!extendTrialTarget}
        onClose={() => setExtendTrialTarget(null)}
      />
      <InviteDemoUserModal
        demo={inviteTarget}
        open={!!inviteTarget}
        onClose={() => setInviteTarget(null)}
      />
      <TakeOverDemoModal
        demo={takeOverTarget}
        open={!!takeOverTarget}
        onClose={() => setTakeOverTarget(null)}
      />
      <PurgeDemoModal
        demo={purgeTarget}
        open={!!purgeTarget}
        onClose={() => setPurgeTarget(null)}
      />
    </>
  )
}
